import { useState } from 'react';
import {
  AuthError,
  confirmSignIn,
  fetchAuthSession,
  signIn,
} from 'aws-amplify/auth';

import { LoadingSpinner } from '@shieldpay/bumblebee/loading-spinner';

import { authLogger } from '../../utilities/config';
import { MfaForm } from '../mfa/mfa-form';
import { MfaSetupForm } from '../mfa/mfa-setup-form';
import { NewPasswordForm } from '../new-password-form/new-password-form';

import { LoginForm } from './login-form';

interface LoginProps {
  // The issuer is the name of the application that is requesting the login
  issuer: string;
  onAuthenticated: ({
    email,
    groups,
  }: {
    email: string;
    groups: Array<string>;
  }) => void;
}

type NextStep =
  | Awaited<ReturnType<typeof signIn>>['nextStep']['signInStep']
  | Awaited<ReturnType<typeof confirmSignIn>>['nextStep'];

const step = {
  DEFAULT: 'DEFAULT',
  ENTER_MFA: 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' satisfies NextStep,
  SETUP_MFA: 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP' satisfies NextStep,
  SETUP_PASSWORD:
    'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED' satisfies NextStep,
  DONE: 'DONE' satisfies NextStep,
} as const;

const trimMfaCode = (code: string) => code.split(' ').join('');

const ONE_TICK_MS = 100; // Currently set to 100 as a 1ms tick was too fast for firefox to handle

export const Login = ({ onAuthenticated, issuer }: LoginProps) => {
  const [totpCode, setTotpCode] = useState<string | undefined>();
  const [username, setUsername] = useState<string | undefined>();

  const [submitting, setSubmitting] = useState(false);

  const [nextStep, setNextStep] = useState<NextStep | 'DEFAULT'>(step.DEFAULT);

  const [errorState, setErrorState] = useState<AuthError['name'] | undefined>();
  const resetErrorState = () => setErrorState(undefined);

  const handleOnAuthenticated = async () => {
    const { tokens } = await fetchAuthSession();

    const payload = tokens?.idToken?.payload;

    if (!payload) {
      throw new Error('handleOnAuthenticated');
    }

    const groups = (payload['cognito:groups'] as string[]) || [];
    const email = payload.email?.toString();

    if (!email) throw new Error('No email');

    authLogger('Authenticated client side', {
      email,
      groups,
    });

    onAuthenticated({
      email,
      groups,
    });

    window.setTimeout(() => {
      // We wait one tick so that the `onAuthenticated` callback can fire off the request to the server
      // before we then remove these document cookies
      document.cookie.split('; ').forEach((cookie) => {
        if (cookie.startsWith('CognitoIdentityServiceProvider')) {
          document.cookie = `${cookie.split('=')[0]}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
        }
      });

      setNextStep(step.DONE);
    }, ONE_TICK_MS);
  };

  if (
    nextStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP' &&
    !(totpCode && username)
  ) {
    throw new Error('Missing totpCode or username during TOTP setup');
  }

  const createHandleStep =
    <Values,>(callback: (values: Values) => Promise<void>) =>
    async (values: Values) => {
      setSubmitting(true);

      try {
        await callback(values);
      } catch (thrown) {
        // eslint-disable-next-line no-console
        console.warn(thrown);

        if (!(thrown instanceof Error || thrown instanceof AuthError)) {
          console.error('Unexpected error type', thrown);

          setNextStep(step.DEFAULT);
          throw thrown;
        }

        if (thrown.name === 'UserUnAuthenticatedException') {
          setNextStep(step.DEFAULT);
          setSubmitting(false);

          return;
        }

        if (thrown.name === 'UserAlreadyAuthenticatedException') {
          // this is probably a rare, or even unlikely, case
          handleOnAuthenticated();
        }

        setErrorState(thrown.message);
      }

      setSubmitting(false);
    };

  return (
    <>
      {nextStep === step.DEFAULT ? (
        <LoginForm
          onSubmit={createHandleStep(async (values) => {
            const { isSignedIn, nextStep } = await signIn(values);

            if (isSignedIn) {
              throw new Error(
                `Unexpected next step when signing in: ${nextStep.signInStep}`,
              );
            }

            if (nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
              setTotpCode(nextStep.totpSetupDetails.sharedSecret);
            }

            setUsername(values.username);
            setNextStep(nextStep.signInStep);
          })}
          submitting={submitting}
          errorState={errorState}
          resetErrorState={resetErrorState}
        />
      ) : null}
      {nextStep === step.ENTER_MFA ? (
        <MfaForm
          onSubmit={createHandleStep(async ({ mfaCode }) => {
            const { isSignedIn, nextStep } = await confirmSignIn({
              challengeResponse: trimMfaCode(mfaCode),
            });

            if (!isSignedIn) {
              throw new Error(
                `Unexpected next step when signing in: ${nextStep.signInStep}`,
              );
            }

            handleOnAuthenticated();
          })}
          submitting={submitting}
          errorState={errorState}
          resetErrorState={resetErrorState}
        />
      ) : null}
      {nextStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED' ? (
        <NewPasswordForm
          onSubmit={createHandleStep(async ({ newPassword }) => {
            const { isSignedIn, nextStep } = await confirmSignIn({
              challengeResponse: newPassword,
            });

            if (isSignedIn) {
              throw new Error(
                `Unexpected next step in new password step: ${nextStep.signInStep}`,
              );
            }

            if (nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
              setTotpCode(nextStep.totpSetupDetails.sharedSecret);
            }

            setNextStep(nextStep.signInStep);
          })}
          submitting={submitting}
        />
      ) : null}
      {nextStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP' ? (
        <MfaSetupForm
          username={username as string}
          totpCode={totpCode as string}
          onSubmit={createHandleStep(async ({ mfaCode }) => {
            const { isSignedIn, nextStep } = await confirmSignIn({
              challengeResponse: trimMfaCode(mfaCode),
            });

            if (!isSignedIn) {
              throw new Error(
                `Unexpected next step during MFA setup ${nextStep.signInStep}`,
              );
            }

            handleOnAuthenticated();
          })}
          submitting={submitting}
          errorState={errorState}
          resetErrorState={resetErrorState}
          issuer={issuer}
        />
      ) : null}
      {nextStep === step.DONE ? <LoadingSpinner center size="xxlarge" /> : null}
    </>
  );
};
